import { warn } from '@ember/debug';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import {
  addDays,
  addWeeks,
  endOfMonth,
  endOfWeek,
  format,
  isBefore,
  setDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import WeekRow from 'garaje/components/week-row';
import type StateService from 'garaje/services/state';
import type ActiveDayConfig from 'garaje/types/date-time/active-day-config';
import type DateLimits from 'garaje/types/date-time/date-limits';
import type DayConfig from 'garaje/types/date-time/day-config';
import { type CalendarDate } from 'garaje/utils/date-fns-tz-utilities';
import {
  DEFAULT_TIMEZONE,
  ensurePlainJsDate,
  isInDateRange,
  isMoment,
  modifyDateInTimeZone,
  sameTimeInTimeZone,
} from 'garaje/utils/date-fns-tz-utilities';
import moment from 'moment-timezone';

interface CalendarGridSignature {
  Args: {
    currentMonth?: CalendarDate;
    events?: CalendarDate[];
    hideLeadingDayCells?: boolean;
    hideTrailingDayCells?: boolean;
    locale?: string;
    respectSchedulingLimits?: boolean;
    selectedDates?: CalendarDate[];
    selectedEndDate?: CalendarDate;
    selectedStartDate?: CalendarDate;
    timezone?: string;
    selectDate?: (date: moment.Moment) => void;
    onSelectDate?: (date: Date) => void;
    daysToCustomize?: Array<DayConfig>;
    dateLimits?: DateLimits;
    activeDays?: Array<ActiveDayConfig>;
  };
  Element: HTMLDivElement;
}

interface WeekConfig {
  startDate: CalendarDate;
  events: CalendarDate[];
}

type DayInt = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;

type WeekInfo = {
  firstDay: DayInt;
  weekend: DayInt[];
  minimalDays: DayInt;
};

type LocaleWithWeekInfo = Intl.Locale & {
  getWeekInfo?: () => WeekInfo;
};

function filterEvents(events: Date[], firstDay: Date, lastDay: Date): Date[] {
  return (events || []).filter((e) => isInDateRange(e, firstDay, lastDay));
}

export default class CalendarGrid extends Component<CalendarGridSignature> {
  // Use @args instead of consuming state service in otherwise portable component
  @service declare state: StateService;

  constructor(...args: ConstructorParameters<typeof Component<CalendarGridSignature>>) {
    super(...args);

    const { currentMonth, events, selectedDates, selectedEndDate, selectedStartDate, selectDate } = this.args;

    if (currentMonth) {
      warn('CalendarGridComponent: @currentMonth uses an instance of Moment.js', !isMoment(currentMonth), {
        id: 'envoy.calendar-grid.moment-instance',
      });
    }

    if (selectedStartDate) {
      warn('CalendarGridComponent: @selectedStartDate uses an instance of Moment.js', !isMoment(selectedStartDate), {
        id: 'envoy.calendar-grid.moment-instance',
      });
    }

    if (selectedEndDate) {
      warn('CalendarGridComponent: @selectedEndDate uses an instance of Moment.js', !isMoment(selectedEndDate), {
        id: 'envoy.calendar-grid.moment-instance',
      });
    }

    warn(
      'CalendarGridComponent: @selectedDates uses an instance of Moment.js',
      !(selectedDates || []).some((date) => isMoment(date)),
      {
        id: 'envoy.calendar-grid.moment-instance',
      },
    );

    warn(
      'CalendarGridComponent: @events uses an instance of Moment.js',
      !(events || []).some((date) => isMoment(date)),
      {
        id: 'envoy.calendar-grid.moment-instance',
      },
    );

    warn('CalendarGridComponent: the @selectDate action is deprecated; use @onSelectDate instead', !selectDate, {
      id: 'envoy.calendar-grid.moment-instance',
    });
  }

  get timezone(): string {
    const { currentLocation } = this.state;
    const { args } = this;

    if (args.timezone) return args.timezone;
    if (currentLocation?.timezone) return currentLocation.timezone;

    return DEFAULT_TIMEZONE;
  }

  get dateLocale(): string {
    return this.args.locale || Intl?.DateTimeFormat()?.resolvedOptions()?.locale || navigator?.language || 'en-US';
  }

  get firstDayOfWeek(): DayInt {
    const { dateLocale } = this;
    const locale = new Intl.Locale(dateLocale) as LocaleWithWeekInfo;

    if (typeof locale.getWeekInfo === 'function') return locale.getWeekInfo().firstDay;

    // Fallback to moment.js localeData for Firefox
    return <DayInt>moment.localeData(dateLocale).firstDayOfWeek();
  }

  get currentMonth(): Date {
    const currentMonth = this.args.currentMonth || new Date();

    return ensurePlainJsDate(currentMonth, this.timezone);
  }

  get events(): Date[] {
    const {
      timezone,
      args: { events = [] },
    } = this;

    return events.map((e) => ensurePlainJsDate(e, timezone));
  }

  get weeksArray(): WeekConfig[] {
    const { timezone, events, currentMonth, firstDayOfWeek: weekStartsOn } = this;

    const weeks: WeekConfig[] = [];
    const firstDayOfMonth = modifyDateInTimeZone(currentMonth, timezone, startOfMonth);
    const lastDayOfMonth = modifyDateInTimeZone(currentMonth, timezone, endOfMonth);
    let firstDayOfWeek = modifyDateInTimeZone(firstDayOfMonth, timezone, startOfWeek, { weekStartsOn });
    let lastDayOfWeek = modifyDateInTimeZone(firstDayOfWeek, timezone, endOfWeek, { weekStartsOn });
    let snapshot;

    while (isBefore(firstDayOfWeek, lastDayOfMonth)) {
      snapshot = new Date(firstDayOfWeek);

      const eventsForWeek = filterEvents(events, addDays(snapshot, -1), addDays(lastDayOfWeek, 1));

      weeks.push({ startDate: snapshot, events: eventsForWeek });

      // Use modifyDateInTimeZone to handle DST transitions when adding weeks
      firstDayOfWeek = modifyDateInTimeZone(firstDayOfWeek, timezone, (d) => addWeeks(d, 1));
      lastDayOfWeek = modifyDateInTimeZone(lastDayOfWeek, timezone, (d) => addWeeks(d, 1));
    }

    return weeks;
  }

  get headerDays(): string[] {
    const { firstDayOfWeek } = this;
    const days = [];

    for (let i = 0; i < 7; i++) {
      const day = format(setDay(new Date(), (firstDayOfWeek + i) % 7), 'EEEEEE');

      days.push(day);
    }

    return days;
  }

  // Convert Date to Moment until consumers updated
  @action
  selectDate(date: Date): void {
    const { selectDate, onSelectDate } = this.args;
    const tz: string = `${moment().tz() || DEFAULT_TIMEZONE}`;

    // Deprecated use of moment.js
    selectDate?.(moment(sameTimeInTimeZone(date, tz, this.timezone)));

    // New action with plain JS Date
    onSelectDate?.(date);
  }

  <template>
    <div class="calendar-grid">
      <table cellpadding="0" cellspacing="0">
        <thead>
          <tr>
            {{#each this.headerDays as |day|}}
              <th class="text-xs font-medium leading-normal text-carbon-40">{{day}}</th>
            {{/each}}
          </tr>
        </thead>
        <tbody>
          {{#each this.weeksArray as |week idx|}}
            <WeekRow
              @currentMonth={{this.currentMonth}}
              @startDate={{week.startDate}}
              @selectedDates={{@selectedDates}}
              @selectedStartDate={{@selectedStartDate}}
              @selectedEndDate={{@selectedEndDate}}
              @hideTrailingDayCells={{@hideTrailingDayCells}}
              @hideLeadingDayCells={{@hideLeadingDayCells}}
              @respectSchedulingLimits={{@respectSchedulingLimits}}
              @events={{week.events}}
              @selectDate={{this.selectDate}}
              @daysToCustomize={{@daysToCustomize}}
              @dateLimits={{@dateLimits}}
              @activeDays={{@activeDays}}
              @timezone={{this.timezone}}
              data-test-calendar-row={{idx}}
            />
          {{/each}}
        </tbody>
      </table>
    </div>
  </template>
}
